/**
 * When a preview is up, decided from ONE source: the runner's sessions of the project as vfs-service lists them
 * (`GET /api/v1/projects/:id/services`, hooks/useProjectServices.js). The header's "Running 1/1", the bottom bar and
 * the Preview tab all read that list, so the Preview can no longer sit on "Rendering…" while the header already says
 * the service runs (the render call that put the tab in loading may have been cancelled, skipped or failed on a
 * response it could not read; see Canvas.jsx's preview watchdog).
 *
 * Also: how a status poll reads the runner's answer (pollRenderStatus in previewSession.js). A 3xx, a 401/403 or a
 * page instead of JSON (a gateway redirect to Flow's own HTML, or to the sign-in) is never "still starting".
 */
import { canonicalServiceName, serviceState } from '../services/SandboxService';

/** Waiting longer than this on "Rendering…" while the service reports running: reload once, then say so. */
export const SLOW_START_MS = 60_000;

const isFailedBuild = (session) => session?.buildStatus === 'failed' || session?.buildStatus === 'error';

/** The runner's session of `serviceName` in a services list ("app", "services/app" and "./services/app" match). */
export function serviceEntryFor(services, serviceName) {
  if (!Array.isArray(services) || !serviceName) { return null; }
  const wanted = canonicalServiceName(serviceName);
  return services.find((service) => service && (
    canonicalServiceName(service.serviceName) === wanted
    || (service.runnerServiceName && canonicalServiceName(service.runnerServiceName) === wanted)
  )) || null;
}

/**
 * Stopped on purpose (Flow's Stop): the runner says so (`stopped: true`) and nothing runs or builds. Whatever else
 * the entry carries (an old failed build, say), the Preview treats it as stopped: never started behind the user's back.
 */
export function isStoppedOnPurpose(session) {
  return Boolean(session) && session.stopped === true && session.containerStatus !== 'running' && session.buildStatus !== 'building';
}

/** The process is up (it may still be finishing its build). */
export function reportsRunning(session) {
  return Boolean(session) && session.containerStatus === 'running' && !session.crashed;
}

/** Up, built and with a URL to show: the Preview can show the app now. */
export function isPreviewReady(session) {
  return reportsRunning(session)
    && session.buildStatus !== 'building'
    && !isFailedBuild(session)
    && typeof session.previewUrl === 'string' && session.previewUrl.length > 0;
}

/** `serviceName`'s session when it is ready to show, else null. */
export function readyServiceFor(services, serviceName) {
  const entry = serviceEntryFor(services, serviceName);
  return isPreviewReady(entry) ? entry : null;
}

/**
 * How to treat one status response:
 * - `ok`: a JSON answer to read;
 * - `gone`: 404, the runner does not know the session (yet);
 * - `signed-out`: 401/403, a redirect (followed or not) or a page instead of JSON: Flow's session, or a route that
 *   does not reach the runner. Never retried forever;
 * - `retry`: 5xx/429 and anything else, worth another try after a pause.
 */
export function classifyStatusResponse(response) {
  if (!response) { return 'retry'; }
  if (response.type === 'opaqueredirect' || response.redirected) { return 'signed-out'; }
  const status = Number(response.status) || 0;
  if (status >= 300 && status < 400) { return 'signed-out'; }
  if (status === 401 || status === 403) { return 'signed-out'; }
  if (status === 404) { return 'gone'; }
  if (!response.ok) { return 'retry'; }
  const type = String(response.headers?.get?.('content-type') || 'application/json');
  return type.includes('json') ? 'ok' : 'signed-out';
}

/** Pause before retry `attempt` (1, 2, …): 1 s, 2 s, 4 s … at most `maxMs`. */
export function backoffDelay(attempt, { baseMs = 1000, maxMs = 15000 } = {}) {
  const n = Math.max(1, Number(attempt) || 1);
  return Math.min(maxMs, baseMs * (2 ** (n - 1)));
}

/** A status poll that cannot go on: the session (or the route to the runner) refuses it. `code`: 'signed_out'. */
export class PreviewStatusError extends Error {
  constructor(message, code, status = null) {
    super(message);
    this.name = 'PreviewStatusError';
    this.code = code;
    this.status = status;
  }
}

/** The tab error the Preview shows as "could not reach the preview" with Retry (not as a build failure). */
export const PREVIEW_UNREACHABLE_PREFIX = '__PREVIEW_UNREACHABLE__:';

/** After the one automatic reload, how long to wait before saying "Taking longer than usual". */
export const AFTER_RELOAD_MS = 15_000;

/**
 * The Preview tabs against the shared service state: what to change so a tab never stays on "Rendering…" while its
 * service runs. Returns `{ [serviceName]: patch }` (empty when nothing changes). `track` (a Map kept by the caller
 * across calls) remembers per tab since when it loads, since when its service reports running and when it was
 * reloaded.
 * - A loading tab whose service is ready (running, built, with a preview URL) in an answer that arrived after the
 *   tab started loading: show it (url, loading off, a fresh iframe).
 * - Loading for SLOW_START_MS while the service reports running but is not ready by the rule above (still marked
 *   building, or no answer newer than the tab): reload once, showing the service's URL when it has one;
 *   AFTER_RELOAD_MS later, still loading: `slowStart` (the Preview says "Taking longer than usual · Refresh · Logs"
 *   instead of spinning silently).
 * @param {object[]} tabs
 * @param {object[]} services the project's services (useProjectServices)
 * @param {{ answerAt: number, now: number, track: Map<string, object> }} context
 */
export function reconcilePreviewTabs(tabs, services, { answerAt, now, track }) {
  const patches = {};
  const seen = new Set();
  for (const tab of Array.isArray(tabs) ? tabs : []) {
    const name = tab?.serviceName;
    if (!name) { continue; }
    seen.add(name);
    // A command-line tool's tab is Try it (components/preview/CliTryIt.jsx): never a page to wait for.
    if (!tab.loading || tab.error || tab.isEmptyService || tab.pullingCode || tab.cli) {
      track.delete(name);
      continue;
    }
    let entry = track.get(name);
    if (!entry) {
      entry = { loadingSince: now, runningSince: null, reloadedAt: null };
      track.set(name, entry);
    }
    const session = serviceEntryFor(services, name);
    const fresh = answerAt > entry.loadingSince;
    if (fresh && isPreviewReady(session)) {
      patches[name] = {
        url: session.previewUrl,
        sessionId: session.sessionId || tab.sessionId,
        loading: false,
        error: null,
        isEmptyService: false,
        slowStart: false,
        renderPhase: undefined,
        renderProgress: undefined,
        renderLogs: undefined,
        iframeKey: now,
        followingBuild: false,
      };
      track.delete(name);
      continue;
    }
    // A tab following a build it did not post (decidePreviewOpen's `follow`): no poll of its own reports a failure,
    // so the shared state does.
    if (tab.followingBuild && fresh && session && (isFailedBuild(session) || (session.crashed && session.buildStatus !== 'building'))) {
      patches[name] = {
        loading: false,
        followingBuild: false,
        error: session.buildError || (session.crashed ? 'The app stopped with an error.' : 'The build failed.'),
        renderPhase: undefined,
        renderProgress: undefined,
      };
      track.delete(name);
      continue;
    }
    if (!reportsRunning(session)) {
      entry.runningSince = null;
      continue;
    }
    if (entry.runningSince === null) { entry.runningSince = now; }
    if (now - entry.runningSince < SLOW_START_MS) { continue; }
    if (entry.reloadedAt === null) {
      entry.reloadedAt = now;
      // Running with a URL: show it (a fresh iframe) rather than keep spinning; without one, only a new iframe key.
      patches[name] = session.previewUrl
        ? { url: session.previewUrl, sessionId: session.sessionId || tab.sessionId, loading: false, renderPhase: undefined, renderProgress: undefined, iframeKey: now }
        : { iframeKey: now };
      if (session.previewUrl) { track.delete(name); }
      continue;
    }
    if (!tab.slowStart && now - entry.reloadedAt >= AFTER_RELOAD_MS) {
      patches[name] = { slowStart: true };
    }
  }
  for (const name of [...track.keys()]) {
    if (!seen.has(name)) { track.delete(name); }
  }
  return patches;
}

/** After a Start from the Preview, how long a "stopped" answer is taken as not caught up yet (not as a failed start). */
export const START_GRACE_MS = 15_000;

/**
 * One word for where a service is, as the Preview needs it, from the shared services list:
 * `ready` (running, built, with a URL) | `starting` (building, or running but not ready yet) | `stopped` (its session
 * is stopped) | `absent` (the runner has no session for it) | `crashed` | `failed`; `null` when the list is not known
 * (an older vfs-service, or the services unavailable): then nothing is decided from it.
 */
export function previewStateOf(services, serviceName, known = true) {
  if (!known) { return null; }
  const entry = serviceEntryFor(services, serviceName);
  if (!entry) { return 'absent'; }
  if (isStoppedOnPurpose(entry)) { return 'stopped'; }
  if (isPreviewReady(entry)) { return 'ready'; }
  const state = serviceState(entry);
  return state === 'building' || state === 'running' ? 'starting' : state;
}

/**
 * The Preview tabs that are NOT rendering, against the shared service state: the Preview follows the service whatever
 * starts or stops it (the in-page Start, the header's Start all, Home, Restart, an AI tool). Returns
 * `{ [serviceName]: patch }`; `follow` (a Map kept by the caller across calls) remembers whether each tab's service
 * was ready at the last answer, and since when the tab is followed: "stopped" is only believed from an answer newer
 * than that (an answer from before a render that just finished still says stopped).
 * - Ready after it was not (or the tab shows the stopped / starting state): show the app, a fresh iframe.
 * - Starting while the tab shows the stopped state: `starting` ("Starting…").
 * - Stopped (or its session gone after it was ready): `stopped`, the stopped state with Start instead of stale
 *   content. Not within START_GRACE_MS of a Start pressed in the Preview (`tab.startRequestedAt`): the list may not
 *   have caught up yet.
 * A loading tab (a render in flight) is left to reconcilePreviewTabs.
 * @param {object[]} tabs
 * @param {object[]} services
 * @param {{ known: boolean, answerAt: number, now: number, follow: Map<string, { ready: (boolean|null), since: number }>,
 *   since?: number }} context `since`: when a tab seen for the first time is followed from (default `now`; the
 *   watchdog passes when it started watching, so the first answer it asked for counts)
 */
export function followPreviewTabs(tabs, services, { known, answerAt, now, follow, since = now }) {
  const patches = {};
  const seen = new Set();
  for (const tab of Array.isArray(tabs) ? tabs : []) {
    const name = tab?.serviceName;
    if (!name) { continue; }
    seen.add(name);
    // Rendering: reconcilePreviewTabs shows it; the next answer after that is a first look. A command-line tool: Try it.
    if (tab.loading || tab.isEmptyService || tab.pullingCode || tab.cli) {
      follow.delete(name);
      continue;
    }
    const state = previewStateOf(services, name, known);
    if (state === null) { continue; }
    let before = follow.get(name);
    if (!before) {
      before = { ready: null, since };
      follow.set(name, before);
    }
    const graced = Boolean(tab.startRequestedAt) && now - tab.startRequestedAt < START_GRACE_MS;
    if (state === 'ready') {
      if (tab.stopped || tab.starting || before.ready === false) {
        const session = serviceEntryFor(services, name);
        patches[name] = {
          url: session.previewUrl,
          sessionId: session.sessionId || tab.sessionId,
          stopped: false,
          starting: false,
          startRequestedAt: null,
          loading: false,
          error: null,
          slowStart: false,
          iframeKey: now,
        };
      }
      before.ready = true;
      continue;
    }
    if (answerAt < before.since) { continue; }
    const wasReady = before.ready === true;
    before.ready = false;
    if (tab.error) { continue; }
    if (state === 'starting') {
      // From the stopped state only: an app on screen keeps showing while it rebuilds, and reloads once ready.
      if (tab.stopped) { patches[name] = { stopped: false, starting: true }; }
      continue;
    }
    if (state === 'stopped' || state === 'absent') {
      if (graced) { continue; }
      // No session at all: stopped only when it went away (it was ready), a Start did not take, or nothing shows.
      const goneOrNothing = state === 'stopped' || wasReady || tab.starting || !tab.url;
      if (goneOrNothing && (!tab.stopped || tab.starting)) {
        patches[name] = { stopped: true, starting: false, startRequestedAt: null };
      }
      continue;
    }
    // Crashed or failed: the runner's own page in the iframe says what happened.
    if (tab.stopped || tab.starting) {
      patches[name] = { stopped: false, starting: false, startRequestedAt: null, iframeKey: now };
    }
  }
  for (const name of [...follow.keys()]) {
    if (!seen.has(name)) { follow.delete(name); }
  }
  return patches;
}

/**
 * Whether a preview page's body is the app's own JSON answer (a backend's `{"ok":true,…}`), as opposed to the runner's
 * JSON error (`{"success":false,…}`, "Preview session not found"). Only a body that parses as JSON counts.
 * @param {string} bodyText
 * @param {string} [contentType] the document's content type, when the browser knows it
 */
export function isAppJsonBody(bodyText, contentType = '') {
  const text = String(bodyText || '').trim();
  const looksJson = String(contentType).includes('json') || text.startsWith('{') || text.startsWith('[');
  if (!looksJson || !text) { return false; }
  let parsed;
  try { parsed = JSON.parse(text); } catch { return false; }
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    if (parsed.success === false) { return false; }
    if (typeof parsed.message === 'string' && /preview session not found/i.test(parsed.message)) { return false; }
  }
  return true;
}

/** What only the runner (or the browser, or a proxy in front of it) writes when a preview did not load. */
const RUNNER_ERROR_MARKERS = [
  'Preview session not found',
  'Failed to proxy request to preview server',
  'error sending request for url',
  'might be temporarily down',
  'may have moved permanently',
  'Preview server is still starting up',
];

/**
 * Whether a preview page is the app's own answer, as opposed to the runner's error: the app's JSON (isAppJsonBody),
 * or a short text answer with no markup (a worker's `res.send('Mongo consumer is running')`, served as text/plain or
 * as text/html with no element). The runner's own pages are full HTML documents titled "… — Flow Preview", or JSON
 * errors; an empty body is never an answer.
 * @param {{ bodyText?: string, bodyHTML?: string, contentType?: string, title?: string }} doc
 */
export function isAppOwnAnswer({ bodyText = '', bodyHTML = '', contentType = '', title = '' } = {}) {
  const text = String(bodyText || '').trim();
  if (!text) { return false; }
  if (isAppJsonBody(text, contentType)) { return true; }
  if (/— Flow Preview$/.test(String(title || ''))) { return false; }
  if (RUNNER_ERROR_MARKERS.some((marker) => text.includes(marker))) { return false; }
  if (/(^|\s)Session\b.*\bnot found/i.test(text)) { return false; }
  const type = String(contentType || '').toLowerCase();
  const html = String(bodyHTML || '');
  if (html.includes('chrome-error://') || html.includes('ERR_CONNECTION')) { return false; }
  // text/plain: the browser wraps it in one <pre>; text/html with no element: the bare text.
  const plainText = type.startsWith('text/plain') || !/<[a-z]/i.test(html.replace(/^\s*<pre[^>]*>|<\/pre>\s*$/gi, ''));
  return plainText && !/^\{|^\[/.test(text);
}
